import React from 'react'
import { Maximize2, X } from 'lucide-react'
import type { KPIInfo } from './KPICard'
import { ChartInfoButton } from './ChartInfoButton'

/**
 * Unified chart panel wrapper used across the analytics surface.
 *
 * Absorbs the repeated "card > header (title/subtitle left, info right) >
 * chart body" pattern that lived inline on every page, and adds a single
 * missing affordance: an expand button that re-renders the chart in a
 * viewport-sized modal so operators can actually read dense series (the
 * heatmap / absorption table / concentration bars had no way to enlarge).
 *
 * The expanded view uses the same body the caller passes in. Pass the
 * chart as children OR via the `render(height)` prop if you need the chart
 * to react to the modal's taller viewport (recommended for ResponsiveContainer).
 */
export interface ChartChromeProps {
  title: string
  subtitle?: React.ReactNode
  info?: { title?: string; data: KPIInfo }
  /** Right-side actions slot (rendered left of the info + expand buttons). */
  actions?: React.ReactNode
  /** Hide the expand affordance when the chart doesn't benefit from more space (KPI lists, counts). */
  expandable?: boolean
  /** Minimum height of the in-card body, px. Defaults to 320 to match the old layout. */
  bodyMinHeight?: number
  /**
   * Render the body. Receives a `height` hint (number) the chart can feed into
   * ResponsiveContainer. In the modal, height is the viewport-based tall value.
   * If you pass `children` instead, the same node renders in both places.
   */
  render?: (height: number) => React.ReactNode
  children?: React.ReactNode
  className?: string
}

const MODAL_HEIGHT = 'calc(100vh - 160px)'

export function ChartChrome({
  title,
  subtitle,
  info,
  actions,
  expandable = true,
  bodyMinHeight = 320,
  render,
  children,
  className,
}: ChartChromeProps) {
  const [expanded, setExpanded] = React.useState(false)

  const renderBody = (height: number) =>
    render ? render(height) : children

  return (
    <>
      <div className={`card ${className ?? ''}`}>
        <div className="flex items-start justify-between mb-3 gap-3">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-secondary-900 truncate">
              {title}
            </h2>
            {subtitle != null && (
              <div className="text-xs text-secondary-500">{subtitle}</div>
            )}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {actions}
            {info && <ChartInfoButton title={info.title ?? title} info={info.data} />}
            {expandable && (
              <button
                type="button"
                onClick={() => setExpanded(true)}
                className="rounded-full p-1 text-secondary-400 hover:bg-secondary-100 hover:text-secondary-700 focus:outline-none focus:ring-2 focus:ring-primary-300"
                aria-label={`Expand ${title}`}
                title="Expand"
              >
                <Maximize2 className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
        <div style={{ minHeight: bodyMinHeight }}>{renderBody(bodyMinHeight)}</div>
      </div>

      {expanded && (
        <ExpandedChartModal
          title={title}
          subtitle={subtitle}
          onClose={() => setExpanded(false)}
        >
          {/* Pass a generous height for ResponsiveContainer consumers; CSS
              also constrains via 100vh so the modal never overflows. */}
          {renderBody(1000)}
        </ExpandedChartModal>
      )}
    </>
  )
}

function ExpandedChartModal({
  title,
  subtitle,
  onClose,
  children,
}: {
  title: string
  subtitle?: React.ReactNode
  onClose: () => void
  children: React.ReactNode
}) {
  React.useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 sm:p-6"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Expanded view of ${title}`}
    >
      <div
        className="relative flex w-full max-w-7xl flex-col overflow-hidden rounded-lg bg-white shadow-xl ring-1 ring-secondary-200"
        style={{ maxHeight: 'calc(100vh - 32px)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b border-secondary-100 px-5 py-3">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-secondary-900 truncate">
              {title}
            </h3>
            {subtitle != null && (
              <div className="text-xs text-secondary-500">{subtitle}</div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-secondary-400 hover:bg-secondary-100 hover:text-secondary-700"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div
          className="overflow-auto px-5 py-4"
          style={{ minHeight: MODAL_HEIGHT }}
        >
          {children}
        </div>
      </div>
    </div>
  )
}
